import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2, ImageOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  getProduct,
  getProductByVariant,
  searchProductsByName,
  searchProductsByUrl,
} from "@/services/productsApi";
import { useDebounce } from "@/hooks/useDebounce";
import { deriveProductListingStats } from "@/lib/products";
import type { Product } from "@/types/products";

/** Detected intent of the current query. */
type SearchMode = "url" | "numeric" | "name" | "idle";

function detectMode(query: string): SearchMode {
  const trimmed = query.trim();
  if (!trimmed) return "idle";
  if (/^https?:\/\//i.test(trimmed) || trimmed.includes("/")) return "url";
  if (/^\d+$/.test(trimmed)) return "numeric";
  if (trimmed.length >= 2) return "name";
  return "idle";
}

interface ProductSearchProps {
  /** Called when the user selects a product from the results. */
  onSelect?: (product: Product) => void;
  /** Optional render-prop that appends an action element to each result row. */
  renderAction?: (product: Product) => React.ReactNode;
  /** Controlled value for the search input. */
  value?: string;
  /** Called when the search input changes. */
  onValueChange?: (value: string) => void;
  placeholder?: string;
}

export function ProductSearch({
  onSelect,
  renderAction,
  value: controlledValue,
  onValueChange,
  placeholder = "Search by product ID, variant ID, name, or URL…",
}: ProductSearchProps) {
  const [internalValue, setInternalValue] = useState("");
  const inputValue = controlledValue ?? internalValue;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (onValueChange) {
        onValueChange(v);
      } else {
        setInternalValue(v);
      }
    },
    [onValueChange],
  );

  const debouncedQuery = useDebounce(inputValue, 300);
  const mode = detectMode(debouncedQuery);

  // Numeric: fire both product + variant lookups in parallel
  const { data: productById, isFetching: loadingById } = useQuery({
    queryKey: ["productSearch", "product-id", debouncedQuery],
    queryFn: () => getProduct(Number(debouncedQuery)),
    enabled: mode === "numeric",
    retry: false,
  });

  const { data: productByVariant, isFetching: loadingByVariant } = useQuery({
    queryKey: ["productSearch", "variant-id", debouncedQuery],
    queryFn: () => getProductByVariant(Number(debouncedQuery)),
    enabled: mode === "numeric",
    retry: false,
  });

  // Name search
  const { data: byName, isFetching: loadingByName } = useQuery({
    queryKey: ["productSearch", "name", debouncedQuery],
    queryFn: () => searchProductsByName(debouncedQuery),
    enabled: mode === "name",
    retry: false,
  });

  // URL search
  const { data: byUrl, isFetching: loadingByUrl } = useQuery({
    queryKey: ["productSearch", "url", debouncedQuery],
    queryFn: () => searchProductsByUrl(debouncedQuery),
    enabled: mode === "url",
    retry: false,
  });

  const isLoading =
    loadingById || loadingByVariant || loadingByName || loadingByUrl;

  // Dedupe numeric results by product id
  let results: Product[] = [];
  if (mode === "numeric") {
    const seen = new Set<number>();
    for (const p of [productById, productByVariant]) {
      if (p) {
        const pid = p.details?.id;
        if (pid == null || !seen.has(pid)) {
          if (pid != null) seen.add(pid);
          results.push(p);
        }
      }
    }
  } else if (mode === "name") {
    results = byName ?? [];
  } else if (mode === "url") {
    results = byUrl ?? [];
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-9"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {results.length > 0 && (
        <ul className="divide-y rounded-md border bg-background">
          {results.map((product) => (
            <ProductSearchRow
              key={product.details?.id ?? product.details?.external_id}
              product={product}
              onSelect={onSelect}
              renderAction={renderAction}
            />
          ))}
        </ul>
      )}

      {!isLoading &&
        mode !== "idle" &&
        results.length === 0 &&
        debouncedQuery && (
          <p className="text-sm text-muted-foreground px-1">
            No products found.
          </p>
        )}
    </div>
  );
}

interface ProductSearchRowProps {
  product: Product;
  onSelect?: ((product: Product) => void) | undefined;
  renderAction?: ((product: Product) => React.ReactNode) | undefined;
}

function ProductSearchRow({
  product,
  onSelect,
  renderAction,
}: ProductSearchRowProps) {
  const { details } = product;
  const { has_available_variants, min_price, max_price } =
    deriveProductListingStats(product);
  const priceRange =
    min_price != null && max_price != null
      ? min_price === max_price
        ? `$${min_price.toLocaleString()}`
        : `$${min_price.toLocaleString()} – $${max_price.toLocaleString()}`
      : null;

  return (
    <li
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect?.(product)}
    >
      <div className="shrink-0 size-10 rounded border bg-muted flex items-center justify-center overflow-hidden">
        {details.image_url ? (
          <img
            src={details.image_url}
            alt={details.name}
            className="size-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <ImageOff className="size-5 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{details.name}</span>
          {details.source && (
            <Badge variant="secondary" className="text-xs shrink-0">
              {details.source_name ?? details.source}
            </Badge>
          )}
          {details.housing && (
            <Badge variant="outline" className="text-xs shrink-0">
              {details.housing}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {details.id != null && <span>ID {details.id}</span>}
          {priceRange && <span>{priceRange}</span>}
          {!has_available_variants && (
            <span className="text-destructive">unavailable</span>
          )}
        </div>
      </div>

      {renderAction && (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {renderAction(product)}
        </div>
      )}
    </li>
  );
}
